import * as React from "react";
import {
  Route, Switch,
  useLocation,
  useRouteMatch
} from "react-router-dom";
import { cloneDeep, flatten, isNil, uniqBy } from "lodash";
import { toast, ToastOptions } from "react-toastify";
import {
  AccessReportsAdminVersionTypeEnum
} from "@app/access-reports/admin";
import { assignTemplateMultiElementOptions, getElementByIdFromTemplate, getPageFromTemplate } from "@app/access-reports/shared/utility";
import { UserModelFromApi } from "@app/admin/user-administration/userModel";
import { ProviderSetVersionModel } from "@app/provider-sets/providerSet";
import { UserContext } from "@app/user/UserContext";
import UserService from "@app/user/userService";
import { focusFirstInteractiveElement } from "@app/shared/utility/focusFirstInteractiveElement";
import ProviderSetService from "@app/provider-sets/providerSetService";
import AccessReportsService from "@app/access-reports/accessReportsService";
import { AccessReportsAdminAddProduct } from "@app/access-reports/admin/AccessReportsAdminAddProduct";
import { AccessReportsAdminDashboard } from "@app/access-reports/admin/AccessReportsAdminDashboard";
import { TemplateUpdate } from "@app/access-reports/admin/template-update/TemplateUpdate";
import { AccessReportsCoverPageElementEnum } from "@app/access-reports/shared/accessReportsCoverPageElementEnum";
import { AccessReportsPrintOptions } from "@app/access-reports/shared/accessReportsPrintOptions";
import { AccessReportsProductDefaultElementsEnum } from "@app/access-reports/shared/accessReportsProductDefaultElementsEnum";
import { AccessReportsWizardElement } from "@app/access-reports/shared/accessReportsWizardElement";
import { AccessReportsWizardModel } from "@app/access-reports/shared/accessReportsWizardModel";
import { AccessReportsWizardOption } from "@app/access-reports/shared/accessReportsWizardOption";
import { AccessReportsWizardPage } from "@app/access-reports/shared/accessReportsWizardPage";
import { AccessReportsWizardProduct } from "@app/access-reports/shared/accessReportsWizardProduct";
import { AccessReportsWizardProviderSet } from "@app/access-reports/shared/accessReportsWizardProviderSet";
import { AccessReportsWizardStepEnum } from "@app/access-reports/shared/accessReportsWizardStepEnum";
import { AccessReportsWizardTemplate } from "@app/access-reports/shared/accessReportsWizardTemplate";
import { AccessReportsWizardVersionModel } from "@app/access-reports/shared/accessReportsWizardVersionModel";

interface AccessReportsAdminRouteParams {
  productId: string;
}

const AccessReportsAdmin: React.FC = () => {
  const user = React.useContext(UserContext);
  const location = useLocation();
  const match = useRouteMatch<AccessReportsAdminRouteParams>();
  const accessReportsService = React.useRef(new AccessReportsService()).current;
  const providerSetService = React.useRef(new ProviderSetService()).current;
  const userService = React.useRef(new UserService()).current;
  const [accessReportsWizard, setAccessReportsWizard] = React.useState<AccessReportsWizardModel>(null);
  const [checkedOutUsername, setCheckedOutUsername] = React.useState<string>(null);
  const [disableUserActions, setDisableUserActions] = React.useState(true);
  const [isCheckedOutByCurrentUser, setIsCheckedOutByCurrentUser] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [selectedVersion, setSelectedVersion] = React.useState<AccessReportsWizardVersionModel>(null);
  const [selectedVersionType, setSelectedVersionType] = React.useState<AccessReportsAdminVersionTypeEnum>(null);

  React.useEffect(() => {
    refreshAccessReportsWizard();
  }, []);

  React.useEffect(() => {
    if (!loading) {
      focusFirstInteractiveElement();
    }
  }, [loading]);

  const onActivateClick = async (): Promise<void> => {
    setLoading(true);
    const currentVersion = accessReportsWizard.accessReportWizardVersions.find(x => !x.checkedOutDate && !!x.dateActivated);

    if (!!currentVersion) {
      await createProviderSetCopies(currentVersion.template);

      const archived: boolean = await archiveVersion(
        currentVersion.accessReportWizardId,
        currentVersion.accessReportWizardVersionId,
      );

      if (archived) {
        // after the current version has been successfully archived,
        // activate the in-progress version
        const activated: boolean = await activateVersion(
          selectedVersion?.accessReportWizardId,
          selectedVersion?.accessReportWizardVersionId,
        );

        if (activated) {
          // once this new version has been activated, check it in
          const checkedIn: boolean = await checkInVersion(
            selectedVersion?.accessReportWizardId,
            selectedVersion?.accessReportWizardVersionId,
          );

          if (checkedIn) {
            const toastOptions: ToastOptions = {
              closeOnClick: true,
              containerId: "z-index-9999",
            };

            toast.info("Wizard Activated", toastOptions);

            // and finally, once it's been checked in, retrieve the template again
            await refreshAccessReportsWizard();
          }
        }
      }
    }
  }

  const onCheckInClick = async (): Promise<void> => {
    setLoading(true);
    // check in the currently-selected in-progress version
    const checkedIn: boolean = await checkInVersion(
      selectedVersion?.accessReportWizardId,
      selectedVersion?.accessReportWizardVersionId,
    );

    if (checkedIn) {
      // once it's been checked in, retrieve the template again
      await refreshAccessReportsWizard();
    }
  }

  const onCheckOutClick = async (): Promise<void> => {
    setLoading(true);
    // if the access reports wizard returns a version of the template that has not been activated,
    // then check that version out, as it is currently in-progress
    const inProgressVersion: AccessReportsWizardVersionModel = accessReportsWizard.accessReportWizardVersions.find(x => !x.dateActivated);

    if (!!inProgressVersion) {
      // check out the template
      const checkedOut: boolean = await checkOutVersion(
        inProgressVersion.accessReportWizardId,
        inProgressVersion.accessReportWizardVersionId,
      );

      if (checkedOut) {
        // once it's been checked out, retrieve the template again
        await refreshAccessReportsWizard();
      }
    }
  }

  const onCoverPageDefaultValueChange = (elementId: string, event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>): void => {
    if (!!selectedVersion) {
      const newSelectedVersion: AccessReportsWizardVersionModel = cloneDeep(selectedVersion);
      const coverPage: AccessReportsWizardPage = newSelectedVersion.template?.pages.find((x) => x.title === AccessReportsWizardStepEnum.CoverPage);

      for (const formGroup of coverPage?.formGroups) {
        const element: AccessReportsWizardElement = formGroup.elements.find((x) => x.elementId === elementId);

        if (!!element) {
          element.defaultValue = event.target.value;

          setSelectedVersion(newSelectedVersion);
        }
      }
    }
  }

  const onCoverPageToggle = (elementId: string, key: keyof AccessReportsWizardElement): void => {
    if (!!selectedVersion) {
      const newSelectedVersion: AccessReportsWizardVersionModel = cloneDeep(selectedVersion);
      const coverPage: AccessReportsWizardPage = newSelectedVersion.template?.pages.find((x) => x.title === AccessReportsWizardStepEnum.CoverPage);

      for (const formGroup of coverPage?.formGroups) {
        const element: AccessReportsWizardElement = formGroup.elements.find((x) => x.elementId === elementId);

        if (!!element) {
          // these elements are checkboxes, so boolean makes sense
          ((element)[key] as boolean) = !((element)[key]);

          setSelectedVersion(newSelectedVersion);
        }
      }
    }
  }

  const onCreateNewVersionClick = async (): Promise<void> => {
    setLoading(true);
    // create a new version of the access reports wizard
    // based off of the currently-activated template
    const currentTemplate: AccessReportsWizardTemplate = accessReportsWizard.accessReportWizardVersions.find(x => !!x.dateActivated)?.template;

    if (!!currentTemplate) {
      const newVersion: AccessReportsWizardVersionModel = await createNewVersion(
        accessReportsWizard.accessReportWizardId,
        currentTemplate,
      );

      if (!!newVersion) {
        setSelectedVersion(newVersion);
        // setSelectedVersion isn't complete by the time refreshAccessReportsWizard is called,
        // so we need to let it know there's a new version to expect
        await refreshAccessReportsWizard(newVersion);
      }
    }
  }

  const onDeleteVersionClick = async (): Promise<void> => {
    setLoading(true);
    // deleted the currently-selected in progress version
    const deleted: boolean = await deleteVersion(
      selectedVersion?.accessReportWizardId,
      selectedVersion?.accessReportWizardVersionId,
    );

    if (deleted) {
      // once it's been deleted, retrieve the template again
      await refreshAccessReportsWizard();
    }
  }

  const onPrintOptionsChange = (formGroupId: number, key: keyof AccessReportsPrintOptions, defaultValue: string | boolean): void => {
    if (!!selectedVersion) {
      const newSelectedVersion: AccessReportsWizardVersionModel = cloneDeep(selectedVersion);
      const printOptionsElement: AccessReportsWizardElement = newSelectedVersion.template?.pages.find(
        (x) => x.title === AccessReportsWizardStepEnum.PrintOptions)?.formGroups.find(
          (x) => x.id === formGroupId)?.elements.find(
            (x) => x.elementId === key);

      if (!!printOptionsElement) {
        printOptionsElement.defaultValue = defaultValue;

        setSelectedVersion(newSelectedVersion);
      }
    }
  }

  const onRemoveProductsClick = async (products: AccessReportsWizardProduct[]): Promise<void> => {
    setLoading(true);
    const newSelectedVersion: AccessReportsWizardVersionModel = cloneDeep(selectedVersion);

    for (const product of products) {
      // remove connections to elements with product-specific defaults
      for (const elementId of Object.values(AccessReportsProductDefaultElementsEnum)) {
        if (elementId !== AccessReportsProductDefaultElementsEnum.ProductType) {
          let page: AccessReportsWizardPage;

          switch (elementId) {
            case AccessReportsProductDefaultElementsEnum.AccessStandard:
            case AccessReportsProductDefaultElementsEnum.CalculationMethod:
            case AccessReportsProductDefaultElementsEnum.DistanceMethod:
              page = getPageFromTemplate(newSelectedVersion.template, AccessReportsWizardStepEnum.AccessAnalysis);

              if (!!page) {
                const element: AccessReportsWizardElement = getElementByIdFromTemplate(newSelectedVersion.template, page.title, elementId);

                if (!!element?.options) {
                  for (const option of element.options) {
                    option.defaultProducts = option.defaultProducts.filter(x => x !== product.id);
                    option.hiddenProducts = option.hiddenProducts.filter(x => x !== product.id);
                  }
                }
              }

              break;
            case AccessReportsProductDefaultElementsEnum.Product:
            case AccessReportsProductDefaultElementsEnum.ReportTitleOne:
            case AccessReportsProductDefaultElementsEnum.ReportTitleTwo:
            case AccessReportsProductDefaultElementsEnum.ReportTitleThree:
              page = getPageFromTemplate(newSelectedVersion.template, AccessReportsWizardStepEnum.CoverPage);

              if (!!page) {
                const element: AccessReportsWizardElement = getElementByIdFromTemplate(newSelectedVersion.template, page.title, elementId);

                if (!!element) {
                  element.options = element.options.filter(x => x.id !== product.id);
                }
              }

              break;
            default:
              break;
          }
        }
      }

      const pageOptionsPage: AccessReportsWizardPage = getPageFromTemplate(newSelectedVersion.template, AccessReportsWizardStepEnum.PageOptions);

      for (const pageFormGroup of pageOptionsPage.formGroups) {
        if (!!pageFormGroup.groupPropertyId) {
          // remove by product defaults and column defaults from pages
          if (!!pageFormGroup.byProductDefaults) {
            pageFormGroup.byProductDefaults = pageFormGroup.byProductDefaults.filter(x => x.productId !== product.id);
          }

          if (!!pageFormGroup.columnDefaults) {
            pageFormGroup.columnDefaults = pageFormGroup.columnDefaults.filter(x => x.productId !== product.id);
          }

          pageFormGroup.disallowedMultipleProducts = pageFormGroup.disallowedMultipleProducts.filter(x => x !== product.id);
          pageFormGroup.hiddenProducts = pageFormGroup.hiddenProducts.filter(x => x !== product.id);
        } else {
          // remove page options
          for (const element of pageFormGroup.elements) {
            element.options = element.options.filter(x => x.id !== product.id);
          }
        }
      }
    }

    await onSaveVersionEvent(newSelectedVersion);
  }

  const onResetPrintSettingsClick = (): void => {
    const newSelectedVersion: AccessReportsWizardVersionModel = cloneDeep(selectedVersion);
    const printOptionsPage: AccessReportsWizardPage = newSelectedVersion.template?.pages.find(x => x.title === AccessReportsWizardStepEnum.PrintOptions);
    const printOptionsElements: AccessReportsWizardElement[] = flatten(printOptionsPage?.formGroups.map(x => x.elements));

    for (const element of printOptionsElements) {
      if (element.originalDefaultValue !== null || element.originalDefaultValue !== undefined) {
        element.defaultValue = element.originalDefaultValue;
      }
    }

    setSelectedVersion(newSelectedVersion);
  }

  const onSaveProductClick = async (template: AccessReportsWizardTemplate): Promise<void> => {
    if (isCheckedOutByCurrentUser) {
      setLoading(true);
      const newSelectedVersion: AccessReportsWizardVersionModel = cloneDeep(selectedVersion);
      newSelectedVersion.template = template;

      await onSaveVersionEvent(newSelectedVersion);
    }
  }

  const onSaveClick = async (): Promise<void> => {
    if (isCheckedOutByCurrentUser) {
      setLoading(true);
      const newSelectedVersion: AccessReportsWizardVersionModel = cloneDeep(selectedVersion);

      await onSaveVersionEvent(newSelectedVersion);
    }
  }

  const onSaveLogosClick = async (logos: AccessReportsWizardOption[], reloadPage: boolean): Promise<void> => {
    if (isCheckedOutByCurrentUser) {
      const handleSaveLogos = async () => {
        const newSelectedVersion: AccessReportsWizardVersionModel = cloneDeep(selectedVersion);
        const coverPage: AccessReportsWizardPage = newSelectedVersion.template?.pages.find(x => x.title === AccessReportsWizardStepEnum.CoverPage);
        const coverPageElements: AccessReportsWizardElement[] = flatten(coverPage?.formGroups.map(x => x.elements));
        const logoElement: AccessReportsWizardElement = coverPageElements.find(x => x.elementId === "logo");

        if (!!logoElement) {
          // find all removed logo ids
          const previousLogoIds: number[] = logoElement.options.map(x => x.id);
          const newLogoIds: number[] = logos.map(x => x.id);
          const removedLogoIds: number[] = previousLogoIds.filter(x => !newLogoIds.includes(x));

          // archive any removed logos
          for (const removedLogoId of removedLogoIds) {
            await archiveLogo(removedLogoId);
          }

          logoElement.options = logos;

          await onSaveVersionEvent(newSelectedVersion);
        }
      }

      if (reloadPage) {
        setLoading(true);
        handleSaveLogos();
      } else {
        handleSaveLogos();
      }
    }
  }

  const onSaveProductsClick = async (products: AccessReportsWizardOption[]): Promise<void> => {
    if (isCheckedOutByCurrentUser) {
      setLoading(true);
      const newSelectedVersion: AccessReportsWizardVersionModel = cloneDeep(selectedVersion);
      const coverPage: AccessReportsWizardPage = newSelectedVersion.template?.pages.find(x => x.title === AccessReportsWizardStepEnum.CoverPage);
      const coverPageElements: AccessReportsWizardElement[] = flatten(coverPage?.formGroups.map(x => x.elements));
      const productElement: AccessReportsWizardElement = coverPageElements.find(x => x.elementId === AccessReportsCoverPageElementEnum.Product);

      if (!!productElement) {
        productElement.options = products;

        await onSaveVersionEvent(newSelectedVersion);
      }
    }
  }

  const onSaveProductTypesClick = async (productTypes: AccessReportsWizardOption[]): Promise<void> => {
    if (isCheckedOutByCurrentUser) {
      setLoading(true);
      const newSelectedVersion: AccessReportsWizardVersionModel = cloneDeep(selectedVersion);
      const coverPage: AccessReportsWizardPage = newSelectedVersion.template?.pages.find(x => x.title === AccessReportsWizardStepEnum.CoverPage);
      const coverPageElements: AccessReportsWizardElement[] = flatten(coverPage?.formGroups.map(x => x.elements));
      const productTypeElement: AccessReportsWizardElement = coverPageElements.find(x => x.elementId === AccessReportsCoverPageElementEnum.ProductType);

      if (!!productTypeElement) {
        productTypeElement.options = productTypes;

        await onSaveVersionEvent(newSelectedVersion);
      }
    }
  }

  const onSelectVersionTypeClick = (newSelectedVersionType: AccessReportsAdminVersionTypeEnum): void => {
    let newSelectedVersion: AccessReportsWizardVersionModel = null;

    switch (newSelectedVersionType) {
      case AccessReportsAdminVersionTypeEnum.CurrentVersion:
        // displays the currently-activated version of the access reports wizard
        const currentVersion: AccessReportsWizardVersionModel = accessReportsWizard.accessReportWizardVersions.find(x => !x.checkedOutDate && !!x.dateActivated);

        if (!!currentVersion) {
          newSelectedVersion = currentVersion;
        }

        break;
      case AccessReportsAdminVersionTypeEnum.InProgressVersion:
        // attempts to retrieve an in-progress version of the access reports wizard;
        // if one does not exist, the user will be given the option to create one
        const inProgressVersion: AccessReportsWizardVersionModel = accessReportsWizard.accessReportWizardVersions.find(x => !x.dateActivated);

        if (!!inProgressVersion) {
          newSelectedVersion = inProgressVersion;
        }

        break;
      default:
        break;
    }

    const shouldDisableUserActions: boolean = getDisableUserActions(isCheckedOutByCurrentUser, newSelectedVersionType);

    setDisableUserActions(shouldDisableUserActions);
    setSelectedVersion(newSelectedVersion);
    setSelectedVersionType(newSelectedVersionType);
  }

  const createNewAccessReportsWizardModel = async (): Promise<AccessReportsWizardModel> => {
    let accessReportsWizard: AccessReportsWizardModel = null;

    // if the access reports wizard model does not exist, first, obtain the template via the "old" way;
    // this api call should be updated to include the retrieval of the "skeleton" template if an "old" way template does not exist
    const accessReportsWizardTemplate: AccessReportsWizardTemplate = await accessReportsService.getAccessReportsTemplate();

    // next, create the new access reports wizard model for this client
    if (!!accessReportsWizardTemplate) {
      accessReportsWizard = await createWizard(accessReportsWizardTemplate);

      // now activate the newly-created version
      if (!!accessReportsWizard) {
        const unactivatedVersion = accessReportsWizard.accessReportWizardVersions.find(x => !x.dateActivated && !x.dateArchived && !x.datePurged);

        if (!!unactivatedVersion) {
          // in order to activate this version, it needs to be checked out
          const checkedOut: boolean = await checkOutVersion(
            unactivatedVersion.accessReportWizardId,
            unactivatedVersion.accessReportWizardVersionId,
          );

          if (checkedOut) {
            const activated: boolean = await activateVersion(
              unactivatedVersion.accessReportWizardId,
              unactivatedVersion.accessReportWizardVersionId
            );

            if (activated) {
              // check it right back in
              const checkedIn: boolean = await checkInVersion(
                unactivatedVersion.accessReportWizardId,
                unactivatedVersion.accessReportWizardVersionId,
              );

              if (checkedIn) {
                // and finally, return the new model
                accessReportsWizard = await accessReportsService.getAccessReportsWizard();
              }
            }
          }
        }
      }
    }

    return accessReportsWizard;
  }

  const activateVersion = async (
    accessReportWizardId: number,
    accessReportWizardVersionId: number,
  ): Promise<boolean> => {
    const activated: boolean = await accessReportsService.activateAccessReportsWizardVersion(
      accessReportWizardId,
      accessReportWizardVersionId
    );

    if (activated) {
      return true;
    } else {
      return false;
    }
  }

  const archiveLogo = async (
    logoId: number,
  ): Promise<void> => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        await accessReportsService.archiveLogo(
          logoId,
        );

        resolve();
      } catch {
        reject();
      }
    });
  }

  const archiveVersion = async (
    accessReportWizardId: number,
    accessReportWizardVersionId: number,
  ): Promise<boolean> => {
    const archived: boolean = await accessReportsService.archiveAccessReportsWizardVersion(
      accessReportWizardId,
      accessReportWizardVersionId
    );

    if (archived) {
      return true;
    } else {
      return false;
    }
  }

  const checkInVersion = async (
    accessReportWizardId: number,
    accessReportWizardVersionId: number,
  ): Promise<boolean> => {
    const checkedIn: boolean = await accessReportsService.checkInAccessReportsWizardVersion(
      accessReportWizardId,
      accessReportWizardVersionId,
    );

    if (checkedIn) {
      return true;
    } else {
      return false;
    }
  }

  const checkOutVersion = async (
    accessReportWizardId: number,
    accessReportWizardVersionId: number,
  ): Promise<boolean> => {
    const checkedOut: boolean = await accessReportsService.checkOutAccessReportsWizardVersion(
      accessReportWizardId,
      accessReportWizardVersionId,
    );

    if (checkedOut) {
      return true;
    } else {
      return false;
    }
  }

  const createNewVersion = async (
    accessReportWizardId: number,
    template: AccessReportsWizardTemplate,
  ): Promise<AccessReportsWizardVersionModel> => {
    const newVersion: AccessReportsWizardVersionModel = await accessReportsService.createNewAccessReportsWizardVersion(
      accessReportWizardId,
      template,
    );

    if (!!newVersion) {
      return newVersion;
    } else {
      return null;
    }
  }

  const createProviderSetCopies = async (
    template: AccessReportsWizardTemplate
  ) => {
    const extractProviderSets = (template: AccessReportsWizardTemplate): AccessReportsWizardProviderSet[] => {
      const coverPage: AccessReportsWizardPage = template.pages.find(x => x.title === AccessReportsWizardStepEnum.CoverPage);
      const coverPageElements: AccessReportsWizardElement[] = flatten(coverPage?.formGroups.map(x => x.elements));
      const productElement: AccessReportsWizardElement = coverPageElements.find(x => x.elementId === AccessReportsCoverPageElementEnum.Product);
      const allProducts: AccessReportsWizardProduct[] = productElement?.options as AccessReportsWizardProduct[];

      return flatten(allProducts.map(x => x.accessCriterion.map(y => y.providerSet)));
    }

    const oldProviderSets: AccessReportsWizardProviderSet[] = extractProviderSets(template);
    const newProviderSets: AccessReportsWizardProviderSet[] = extractProviderSets(selectedVersion.template);
    const providerSetsToCopy: AccessReportsWizardProviderSet[] = uniqBy(newProviderSets.filter(x => !oldProviderSets.map(y => y.providerSetId).includes(x.providerSetId)), "providerSetId");
    const nonVersionedProviderSetIds: number[] = providerSetsToCopy.filter(x => !x.providerSetVersionId).map(x => x.providerSetId);

    if (nonVersionedProviderSetIds.length) {
      const providerSetVersionModels: ProviderSetVersionModel[] = await providerSetService.getLatestProviderSetVersion(nonVersionedProviderSetIds, user.clientId);

      if (!!providerSetVersionModels.length) {
        for (const providerSetVersionModel of providerSetVersionModels) {
          const providerSetToUpdate: AccessReportsWizardProviderSet = providerSetsToCopy.find(x => x.providerSetId === providerSetVersionModel.providerSetId);

          if (!!providerSetToUpdate) {
            providerSetToUpdate.providerSetVersionId = providerSetVersionModel.providerSetVersionId;
          }
        }
      }
    }

    for (const providerSet of providerSetsToCopy) {
      await accessReportsService.createProviderSetCopy(providerSet);
    }
  }

  const createWizard = async (
    template: AccessReportsWizardTemplate
  ): Promise<AccessReportsWizardModel> => {
    const accessReportsWizard: AccessReportsWizardModel = await accessReportsService.createAccessReportsWizard(template);

    if (!!accessReportsWizard) {
      return accessReportsWizard;
    } else {
      return null;
    }
  }

  const deleteVersion = async (
    accessReportWizardId: number,
    accessReportWizardVersionId: number,
  ): Promise<boolean> => {
    const deleted: boolean = await accessReportsService.deleteAccessReportsWizardVersion(
      accessReportWizardId,
      accessReportWizardVersionId,
    );

    if (deleted) {
      return true;
    } else {
      return false;
    }
  }

  const getDisableUserActions = (
    isCheckedOutByCurrentUser: boolean,
    selectedVersionType: AccessReportsAdminVersionTypeEnum,
  ): boolean => {
    if (selectedVersionType === AccessReportsAdminVersionTypeEnum.CurrentVersion || !isCheckedOutByCurrentUser) {
      return true;
    } else {
      return false;
    }
  }

  const getWizard = async (): Promise<AccessReportsWizardModel> => {
    let accessReportsWizard: AccessReportsWizardModel = null;

    try {
      accessReportsWizard = await accessReportsService.getAccessReportsWizard();
    }
    // the catch variable is _only_ allowed to be any or unknown in TS as of version 4.4
    catch (e: unknown) {
      if (!accessReportsWizard && (e as { status: number }).status === 404) {
        accessReportsWizard = await createNewAccessReportsWizardModel();
      }
    }

    return accessReportsWizard;
  }

  const onSaveVersionEvent = async (selectedVersion: AccessReportsWizardVersionModel): Promise<void> => {
    const savedAccessReportsWizardModel: AccessReportsWizardVersionModel = await saveVersion(
      selectedVersion.accessReportWizardId,
      selectedVersion.accessReportWizardVersionId,
      selectedVersion.template,
    );

    if (!!savedAccessReportsWizardModel) {
      await refreshAccessReportsWizard();
    }
  }

  const saveVersion = async (
    accessReportWizardId: number,
    accessReportWizardVersionId: number,
    template: AccessReportsWizardTemplate,
  ): Promise<AccessReportsWizardVersionModel> => {
    const savedAccessReportsWizardModel: AccessReportsWizardVersionModel = await accessReportsService.saveAccessReportsWizardVersion(
      accessReportWizardId,
      accessReportWizardVersionId,
      template,
    );

    if (!!savedAccessReportsWizardModel) {
      return savedAccessReportsWizardModel;
    } else {
      return null;
    }
  }

  const refreshAccessReportsWizard = async (forcedNewVersion?: AccessReportsWizardVersionModel): Promise<void> => {
    const newAccessReportsWizard: AccessReportsWizardModel = await getWizard();

    for (const version of newAccessReportsWizard.accessReportWizardVersions) {
      const assignedTemplate: AccessReportsWizardTemplate = assignTemplateMultiElementOptions(version.template);
      version.template = assignedTemplate;
    }

    let newCheckedOutUsername = "";
    let newIsCheckedOutByCurrentUser = false;
    let newSelectedVersion: AccessReportsWizardVersionModel = forcedNewVersion ?? selectedVersion;
    let newSelectedVersionType: AccessReportsAdminVersionTypeEnum = selectedVersionType;

    if (!!newAccessReportsWizard) {
      // maybe to-do: add some logic in here that creates a new version if there are none at all?
      // temporary, to account for null versions being returned:
      newAccessReportsWizard.accessReportWizardVersions = newAccessReportsWizard.accessReportWizardVersions.filter(x => !!x);

      if (location.pathname.includes("product")) {
        // when refreshing inside the product wizard
        newSelectedVersion = newAccessReportsWizard.accessReportWizardVersions.find(x => !x.dateActivated);
        newSelectedVersionType = AccessReportsAdminVersionTypeEnum.InProgressVersion;
      } else if (!!newSelectedVersion) {
        // if a selected version exists, update it
        const updatedSelectedVersion = newAccessReportsWizard.accessReportWizardVersions.find(x => x.accessReportWizardVersionId === newSelectedVersion.accessReportWizardVersionId);

        if (!!updatedSelectedVersion && !updatedSelectedVersion.dateActivated) {
          newSelectedVersion = updatedSelectedVersion;
        } else {
          // deselect the selected version and version type if this update was triggered by an activation
          newSelectedVersion = null;
          newSelectedVersionType = null;
        }
      }

      const checkedOutVersion: AccessReportsWizardVersionModel = newAccessReportsWizard.accessReportWizardVersions.find(x => !!x.checkedOutDate);

      // determine who, if anyone, has checked out a version
      if (!!checkedOutVersion && checkedOutVersion.checkedOutUserId !== null) {
        if (checkedOutVersion.checkedOutUserId === user?.userId) {
          newCheckedOutUsername = user?.displayName ? user.displayName : `User ${user.userId.toString()}`;
          newIsCheckedOutByCurrentUser = true;
        } else {
          // if the currently checked-out template is checked out by a user different than the current one,
          // retrieve the user who checked-out the template's name in order to let the current user know
          if (!isNil(checkedOutVersion.checkedOutUserId)) {
            const checkedOutUser: UserModelFromApi = await userService.getUser(checkedOutVersion.checkedOutUserId);

            if (!!checkedOutUser) {
              newCheckedOutUsername = checkedOutUser.formalName ? checkedOutUser.formalName : `User ${checkedOutUser.userId.toString()}`;
            }
          }
        }
      }
    }

    const shouldDisableUserActions: boolean = getDisableUserActions(newIsCheckedOutByCurrentUser, newSelectedVersionType);

    setAccessReportsWizard(newAccessReportsWizard);
    setCheckedOutUsername(newCheckedOutUsername);
    setDisableUserActions(shouldDisableUserActions);
    setIsCheckedOutByCurrentUser(newIsCheckedOutByCurrentUser);
    setLoading(false);
    setSelectedVersion(newSelectedVersion);
    setSelectedVersionType(newSelectedVersionType);
  }

  return (
    <Switch>
      <Route
        exact={true}
        path={`${match.url}/dashboard`}
        render={(props) =>
          <AccessReportsAdminDashboard
            {...props}
            accessReportsWizard={accessReportsWizard}
            checkedOutUsername={checkedOutUsername}
            disableUserActions={disableUserActions}
            onCheckInClick={onCheckInClick}
            onCheckOutClick={onCheckOutClick}
            isCheckedOutByCurrentUser={isCheckedOutByCurrentUser}
            loading={loading}
            onActivateClick={onActivateClick}
            onCoverPageDefaultValueChange={onCoverPageDefaultValueChange}
            onCoverPageToggle={onCoverPageToggle}
            onCreateNewVersionClick={onCreateNewVersionClick}
            onDeleteVersionClick={onDeleteVersionClick}
            onPrintOptionsChange={onPrintOptionsChange}
            onRemoveProductsClick={onRemoveProductsClick}
            onResetPrintSettingsClick={onResetPrintSettingsClick}
            onSaveClick={onSaveClick}
            onSaveLogosClick={onSaveLogosClick}
            onSaveProductsClick={onSaveProductsClick}
            onSaveProductTypesClick={onSaveProductTypesClick}
            onSelectVersionTypeClick={onSelectVersionTypeClick}
            selectedVersion={selectedVersion}
            selectedVersionType={selectedVersionType}
          />
        }
      />
      <Route
        path={`${match.url}/product/:productId`}
        render={(props) =>
          <AccessReportsAdminAddProduct
            {...props}
            productId={parseInt((props.match.params).productId, 10)}
            disableUserActions={disableUserActions}
            onSaveProductClick={onSaveProductClick}
            template={selectedVersion?.template}
          />
        }
      />
      <Route
        path={`${match.url}/template`}
        component={TemplateUpdate}
      />
    </Switch>
  );
}

export { AccessReportsAdmin };
